import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const EmailChangeCallbackPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleEmailChange = async () => {
      try {
        // Refresh session to get updated user data
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        if (session?.user?.email) {
          setStatus('success');
          setMessage(`Your email has been successfully changed to ${session.user.email}`);
          
          // Determine redirect based on user metadata
          const userType = session.user.user_metadata?.user_type || 'seller';
          
          // Redirect after 2 seconds
          setTimeout(() => {
            navigate(`/${userType}/profile`, { 
              state: { emailChangeSuccess: true }
            });
          }, 2000);
        } else {
          throw new Error('Session not found');
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(error.message || 'Email change verification failed');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/seller/login');
        }, 3000);
      }
    };

    handleEmailChange();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          {status === 'processing' && (
            <div className="text-center">
              <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
              <h2 className="text-xl font-semibold mb-2">Verifying Email Change</h2>
              <p className="text-muted-foreground">Please wait...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
              <h2 className="text-xl font-semibold mb-2 text-green-600">Email Changed!</h2>
              <p className="text-muted-foreground">{message}</p>
              <p className="text-sm text-muted-foreground mt-4">Redirecting to your profile...</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-center">
              <XCircle className="h-12 w-12 mx-auto mb-4 text-red-600" />
              <h2 className="text-xl font-semibold mb-2 text-red-600">Verification Failed</h2>
              <p className="text-muted-foreground">{message}</p>
              <p className="text-sm text-muted-foreground mt-4">Redirecting to login...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailChangeCallbackPage;
